/**
 * HelpersModal component - Modal for selecting helper toggles (multi-select)
 */

import { createDropdown, type DropdownAPI } from "@UI/Dropdown/index.js";
import { getModalManager } from "@UI/ModalManager/index.js";
import type { HelperType } from "../../Helpers.js";

export interface HelpersModalConfig {
  readonly anchorElement: HTMLElement;
  readonly selected: readonly HelperType[];
  readonly allowed?: readonly HelperType[];
  readonly onChange: (selected: readonly HelperType[]) => void;
  readonly onClose?: () => void;
}

export interface HelpersModalAPI {
  readonly show: () => void;
  readonly destroy: () => void;
}

export const createHelpersModal = (
  config: HelpersModalConfig
): HelpersModalAPI => {
  const { anchorElement, selected, allowed, onChange, onClose } = config;

  let isDestroyed = false;
  let dropdownAPI: DropdownAPI | null = null;

  const options: readonly { id: HelperType; label: string; icon: string }[] = [
    { id: "rulers", label: "Rulers", icon: "helpers_rulers" },
    { id: "snap", label: "Snap", icon: "helpers_snap" },
    { id: "guides", label: "Guides", icon: "helpers_guides" },
    { id: "dots", label: "Dots", icon: "helpers_dots" },
    { id: "lines", label: "Lines", icon: "helpers_lines" },
    { id: "overlay", label: "Overlay", icon: "helpers_overlay" },
  ] as const;

  const createDropdownForHelpers = (): DropdownAPI => {
    const allowedSet = allowed && allowed.length ? new Set(allowed) : null;
    const dropdownData = options
      .filter((opt) => (allowedSet ? allowedSet.has(opt.id) : true))
      .map((opt) => ({
        id: opt.id,
        label: opt.label,
        icon: opt.icon,
        selected: selected.includes(opt.id),
      }));

    return createDropdown({
      data: dropdownData,
      multiSelect: true,
      anchorElement,
      maxHeight: "300px",
      onSelectionChange: (selectedIds) => {
        const next = selectedIds as HelperType[];
        onChange(next);
      },
      onClose: () => {
        onClose?.();
      },
    });
  };

  const show = (): void => {
    if (isDestroyed) return;
    if (!dropdownAPI) dropdownAPI = createDropdownForHelpers();

    const rect = anchorElement.getBoundingClientRect();
    const modalManager = getModalManager();
    modalManager.show({
      content: dropdownAPI.getElement(),
      anchor: { top: rect.top, right: window.innerWidth - rect.right },
      offset: { x: 0, y: 40 },
      closeOnBackdrop: true,
      showBackdrop: true,
      onClose: () => onClose?.(),
    });
  };

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    dropdownAPI?.destroy();
    dropdownAPI = null;
  };

  return { show, destroy };
};
